import ActivityPriorityLevel from '../../../enums/activities/ActivityPriorityLevel';
import { AgentDrawerModel, AgentDrawerSortModel } from '../types/types';
import ActivityStatus from '../../../enums/activities/ActivityStatus';

/**
 * Groups and sorts a list of AgentDrawerModel items based on the provided filter and sort model.
 *
 * @param {AgentDrawerModel[] | undefined} data - The list of activity items to be grouped and sorted.
 * @param {Partial<AgentDrawerSortModel>} model - The filter and sorting options, including:
 *   - `SortByPropName`: The property name to sort by ('priority', 'status', 'flsq', or 'company').
 *   - `GroupByPropName`: The property name to group by ('grouped flsqs', 'company', or 'none').
 *   - `Desc`: Boolean indicating descending sort order.
 *
 * @returns {Record<string, AgentDrawerModel[]>} A record where:
 *   - Keys are group identifiers (e.g., company name or DuplicationGroupIdKey).
 *   - Values are arrays of sorted AgentDrawerModel items within each group.
 *
 * ## Sorting behavior:
 * - If `SortByPropName` is not specified, falls back to a default sorting based on:
 *   - `ActivityStatus` tier (e.g., Holding, WaitingForPayment)
 *   - `ActivityPriorityLevel` (e.g., Urgent, Today)
 *
 * ## Grouping behavior:
 * - If no `GroupByPropName` is specified or set to `'none'`, all items are returned in a single group (`''`).
 * - If `GroupByPropName` is `'grouped flsqs'`, items are grouped by `DuplicationGroupIdKey`.
 * - If `GroupByPropName` is `'company'`, items are grouped by `CompanyName`.
 *
 * ## Notes:
 * - Items with missing or undefined grouping keys will be placed under `'No Group'` or `'No Company'`.
 */

/**
 * Sorts a list of AgentDrawerModel items by ActivityPriorityLevel with special handling for Holding and WaitingForPayment statuses.
 * Can also sort by company or FLSQ if specified in the filter model.
 *
 * @param {AgentDrawerModel[]} data - The list of activity items to be sorted.
 * @param {Partial<AgentDrawerSortModel>} model - The filter and sorting options.
 * @returns {AgentDrawerModel[]} A sorted array where:
 *   - If sorting by 'company' or 'flsq': Items are sorted by the specified field
 *   - Otherwise: Items are primarily sorted by ActivityPriorityLevel (Urgent → Today → Tomorrow → Upcoming → Normal → None)
 *   - Within each priority, items with Holding or WaitingForPayment status are placed at the bottom
 *   - The relative order within each priority is maintained for both normal and deprioritized statuses
 */
export function sortActivitiesByPriorityLevel(
  data: AgentDrawerModel[],
  model: Partial<AgentDrawerSortModel> = {}
): AgentDrawerModel[] {
  if (!data) return [];

  const sortKey = model.SortByPropName?.toLowerCase();
  const isDesc = model.Desc ?? false;

  // Handle company or flsq sorting
  if (sortKey === 'company' || sortKey === 'flsq') {
    return [...data].sort((a, b) => {
      let valueA: string | number;
      let valueB: string | number;

      if (sortKey === 'company') {
        valueA = (a.CompanyName || '').toLowerCase();
        valueB = (b.CompanyName || '').toLowerCase();
      } else {
        valueA = a.Flsq ?? 0;
        valueB = b.Flsq ?? 0;
      }

      const direction = isDesc ? -1 : 1;

      if (valueA < valueB) return -1 * direction;
      if (valueA > valueB) return 1 * direction;
      return 0;
    });
  }

  // Default sorting by priority with deprioritized statuses
  const priorityLevelOrder: Partial<Record<ActivityPriorityLevel, number>> = {
    [ActivityPriorityLevel.Urgent]: 0,
    [ActivityPriorityLevel.Today]: 1,
    [ActivityPriorityLevel.Tomorrow]: 2,
    [ActivityPriorityLevel.Upcoming]: 3,
    [ActivityPriorityLevel.Normal]: 4,
    [ActivityPriorityLevel.None]: 5
  };

  const deprioritizedStatuses = new Set([ActivityStatus.Holding, ActivityStatus.WaitingForPayment]);

  return [...data].sort((a, b) => {
    const priorityLevelA = priorityLevelOrder[a.ActivityPriorityLevel] ?? 99;
    const priorityLevelB = priorityLevelOrder[b.ActivityPriorityLevel] ?? 99;

    const isDeprioritizedA = deprioritizedStatuses.has(a.ActivityStatus) ? 1 : 0;
    const isDeprioritizedB = deprioritizedStatuses.has(b.ActivityStatus) ? 1 : 0;

    // Primary sort: priority
    if (priorityLevelA !== priorityLevelB) {
      return priorityLevelA - priorityLevelB;
    }

    // Secondary sort: deprioritized statuses go to bottom within same priority
    if (isDeprioritizedA !== isDeprioritizedB) {
      return isDeprioritizedA - isDeprioritizedB;
    }

    // Tertiary sort: maintain original priority order for deprioritized items
    return priorityLevelA - priorityLevelB;
  });
}
